import { motion } from 'framer-motion'
import { ArrowDown, Download, Mail } from 'lucide-react'
import { Link } from 'react-router-dom'
import { HERO } from '../constants/testIds'

function LineReveal({ children, delay = 0, className = '' }) {
  return (
    <div className={`overflow-hidden ${className}`}>
      <motion.div
        initial={{ y: '105%' }}
        animate={{ y: 0 }}
        transition={{ duration: 1, delay, ease: [0.16, 1, 0.3, 1] }}
      >
        {children}
      </motion.div>
    </div>
  )
}

const statCards = [
  { label: 'Stores Under Management', value: '4+', desc: 'Active multi-store IT operations, Melbourne', delay: 0.55 },
  { label: 'Critical Infrastructure', value: 'CBK', desc: 'Central Bank of Kuwait — Data Center', delay: 0.7 },
  { label: 'Countries', value: '3', desc: 'India · Kuwait · Australia', delay: 0.85 },
]

export default function Hero({ onNavigate }) {
  return (
      <section
      id="hero"
      data-testid={HERO.section}
      className="relative min-h-screen flex items-center bg-[#FAFAFA] overflow-hidden"
    >
      {/* Subtle dot grid */}
      <div
        className="absolute inset-0 z-0 pointer-events-none"
        style={{
          backgroundImage: 'radial-gradient(circle, #D1D5DB 1px, transparent 1px)',
          backgroundSize: '28px 28px',
        }}
      />
      {/* Fade gradient at bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-40 bg-gradient-to-t from-[#FAFAFA] to-transparent z-0 pointer-events-none" />

      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-12 w-full pt-20 pb-16 md:pt-28 md:pb-20">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center min-h-[calc(100vh-7rem)]">

          {/* Left: Typography */}
          <div className="lg:col-span-7">
            {/* Role badge */}
            <motion.div
              initial={{ opacity: 0, x: -16 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              className="inline-flex items-center gap-2 mb-8 px-3 py-1.5 border border-[#0047AB]/20 bg-[#EBF2FF] rounded-full"
            >
              <span className="w-1.5 h-1.5 rounded-full bg-[#0047AB]" />
              <span className="font-mono text-[#0047AB] text-xs uppercase tracking-[0.2em]">IT Support & Implementation</span>
            </motion.div>

            {/* Heading */}
            <h1 data-testid={HERO.heading} className="mb-8">
              <LineReveal delay={0.2}>
                <span className="block font-sans font-bold text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-7xl leading-[0.92] tracking-tight text-[#111111]">
                  BUILDING THE
                </span>
              </LineReveal>
              <LineReveal delay={0.33}>
                <span className="block font-sans font-bold text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-7xl leading-[0.92] tracking-tight text-[#111111]">
                  INFRASTRUCTURE
                </span>
              </LineReveal>
              <LineReveal delay={0.46}>
                <span className="block font-sans font-bold text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-7xl leading-[0.92] tracking-tight text-[#111111]">
                  THAT KEEPS
                </span>
              </LineReveal>
              <LineReveal delay={0.59}>
                <span className="block font-sans font-bold text-4xl sm:text-5xl lg:text-[4.5rem] xl:text-7xl leading-[0.92] tracking-tight">
                  <span className="text-[#111111]">BUSINESS </span>
                  <span className="text-[#0047AB]">RUNNING.</span>
                </span>
              </LineReveal>
            </h1>

            {/* Sub text */}
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="font-body text-[#6B7280] text-base md:text-lg max-w-xl mb-10 leading-relaxed"
            >
              Akshay Rao — From CCTV to CRM, I design, deploy, and support the
              full tech stacks that businesses run on. Three countries. One standard of work.
            </motion.p>

            {/* CTAs */}
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.0, duration: 0.5 }}
              className="flex flex-wrap items-center gap-4"
            >
              <button
                data-testid={HERO.viewWorkBtn}
                onClick={() => onNavigate('work')}
                className="px-7 py-3.5 bg-[#111111] text-white text-sm font-semibold rounded-lg hover:bg-[#0047AB] transition-colors duration-200 flex items-center gap-2"
              >
                View Work
                <ArrowDown size={14} />
              </button>
              <button
                data-testid={HERO.contactBtn}
                onClick={() => onNavigate('contact')}
                className="px-7 py-3.5 border border-[#E5E7EB] bg-white text-[#374151] text-sm font-semibold rounded-lg hover:border-[#0047AB]/40 hover:text-[#0047AB] hover:bg-[#EBF2FF] transition-all duration-200 flex items-center gap-2 shadow-sm"
              >
                <Mail size={14} />
                Contact
              </button>
              <Link
                to="/resume"
                data-testid={HERO.resumeBtn}
                className="px-5 py-3.5 text-[#9CA3AF] hover:text-[#374151] text-sm transition-colors duration-200 flex items-center gap-2 font-body"
              >
                <Download size={14} />
                Resume
              </Link>
            </motion.div>
          </div>

          {/* Right: Floating stat cards */}
          <div className="hidden lg:flex lg:col-span-5 flex-col items-start gap-4 pl-8">
            {statCards.map((card, i) => (
              <motion.div
                key={card.label}
                initial={{ opacity: 0, y: 30, x: 20 }}
                animate={{ opacity: 1, y: 0, x: 0 }}
                transition={{ delay: card.delay, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className={`w-full max-w-xs bg-white border border-[#E5E7EB] rounded-xl p-6 shadow-card hover:shadow-card-hover transition-shadow duration-300 ${
                  i === 1 ? 'ml-8' : i === 2 ? 'ml-4' : ''
                }`}
              >
                <div className="font-mono text-[#0047AB] text-[10px] uppercase tracking-[0.25em] mb-2">{card.label}</div>
                <div className="font-sans font-bold text-5xl text-[#111111] mb-1.5">{card.value}</div>
                <div className="font-body text-[#6B7280] text-sm leading-snug">{card.desc}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </div>

      {/* Scroll indicator — desktop only */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.5 }}
        className="absolute bottom-8 right-10 hidden md:flex flex-col items-center gap-3 z-10"
      >
        <div className="w-px h-10 bg-gradient-to-b from-transparent to-[#D1D5DB] overflow-hidden relative">
          <motion.div
            className="absolute top-0 w-full h-4 bg-[#0047AB]"
            animate={{ y: ['0%', '250%'] }}
            transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
          />
        </div>
        <span className="font-mono text-[#9CA3AF] text-[10px] tracking-[0.3em]">SCROLL</span>
      </motion.div>

      {/* Location badge — desktop only */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.2 }}
        className="absolute top-24 right-8 md:right-12 z-10 hidden md:block text-right"
      >
        <div className="font-mono text-xs text-[#9CA3AF]">
          <div>−37.81° S, 144.96° E</div>
          <div className="text-[#0047AB] font-medium mt-0.5">MELBOURNE</div>
        </div>
      </motion.div>
    </section>
  )
}
